import { BEEP_COMMAND_ADVANCED, BEEP_COMMAND_SIMPLE, FULL_CUT, LINE_FEED, PRINTER_INIT } from './escpos-codes'

const BASE_COLUMNS = 42
const ESC = 0x1b
const GS = 0x1d

// PC850 is the page most ESC/POS printers use for French (ESC t 2).
const PC850: { [code: number]: number } = {
    0xc7: 0x80, 0xfc: 0x81, 0xe9: 0x82, 0xe2: 0x83, 0xe4: 0x84, 0xe0: 0x85, 0xe5: 0x86, 0xe7: 0x87,
    0xea: 0x88, 0xeb: 0x89, 0xe8: 0x8a, 0xef: 0x8b, 0xee: 0x8c, 0xec: 0x8d, 0xc4: 0x8e, 0xc5: 0x8f,
    0xc9: 0x90, 0xe6: 0x91, 0xc6: 0x92, 0xf4: 0x93, 0xf6: 0x94, 0xf2: 0x95, 0xfb: 0x96, 0xf9: 0x97,
    0xff: 0x98, 0xd6: 0x99, 0xdc: 0x9a, 0xf8: 0x9b, 0xa3: 0x9c, 0xd8: 0x9d, 0xd7: 0x9e,
    0xe1: 0xa0, 0xed: 0xa1, 0xf3: 0xa2, 0xfa: 0xa3, 0xf1: 0xa4, 0xd1: 0xa5,
    0xc1: 0xb5, 0xc2: 0xb6, 0xc0: 0xb7,
    0xe3: 0xc6, 0xc3: 0xc7, 0xca: 0xd2, 0xcb: 0xd3, 0xc8: 0xd4, 0xcd: 0xd6, 0xce: 0xd7, 0xcf: 0xd8, 0xcc: 0xde,
    0xd3: 0xe0, 0xd4: 0xe2, 0xd2: 0xe3, 0xf5: 0xe4, 0xd5: 0xe5, 0xda: 0xe9, 0xdb: 0xea, 0xd9: 0xeb,
    0xfd: 0xec, 0xdd: 0xed,
}

export function encodePc850(text: string): number[] {
    const bytes: number[] = []
    for (const ch of text) {
        const code = ch.codePointAt(0) ?? 0x3f
        if (code === 0x20ac) {
            bytes.push(0x45)
            continue
        }
        if (code === 0x0153) {
            bytes.push(0x6f, 0x65)
            continue
        }
        if (code === 0x0152) {
            bytes.push(0x4f, 0x45)
            continue
        }
        if (code <= 0x7f) {
            bytes.push(code)
            continue
        }
        const mapped = PC850[code]
        if (mapped !== undefined) {
            bytes.push(mapped)
            continue
        }
        const folded = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        for (const part of folded || '?') {
            const ascii = part.codePointAt(0) ?? 0x3f
            bytes.push(ascii <= 0x7f ? ascii : 0x3f)
        }
    }
    return bytes
}

function clampSize(value: number): number {
    if (!Number.isFinite(value)) return 1
    return Math.min(8, Math.max(1, Math.round(value)))
}

export function columnsFor(width: number): number {
    const full = Math.max(16, Math.floor(BASE_COLUMNS / clampSize(width)))
    return Math.max(16, full - 2)
}

function wrapWords(text: string, maxWidth: number): string[] {
    const clean = String(text || '').replace(/\s+/g, ' ').trim()
    if (!clean) return ['']
    if (maxWidth < 1) return [clean]
    const lines: string[] = []
    let current = ''
    for (const word of clean.split(' ')) {
        if (word.length > maxWidth) {
            if (current) {
                lines.push(current)
                current = ''
            }
            for (let i = 0; i < word.length; i += maxWidth) lines.push(word.slice(i, i + maxWidth))
            continue
        }
        const next = current ? `${current} ${word}` : word
        if (next.length <= maxWidth) current = next
        else {
            if (current) lines.push(current)
            current = word
        }
    }
    if (current) lines.push(current)
    return lines.length ? lines : ['']
}

export class EscposText {
    private bytes: number[] = []
    private align = -1
    private size = -1
    private spacing = -1

    constructor(emptyLines = 0) {
        this.bytes.push(...PRINTER_INIT)
        this.bytes.push(ESC, 0x74, 2)
        this.bytes.push(ESC, 0x45, 0x01)
        this.bytes.push(ESC, 0x4d, 0x00)
        for (let i = 0; i < Math.max(1, emptyLines); i++) this.bytes.push(0x0d, LINE_FEED)
    }

    private ensure(align: number, width: number, height: number) {
        if (this.align !== align) {
            this.bytes.push(ESC, 0x61, align)
            this.align = align
        }
        const nextSize = ((clampSize(width) - 1) << 4) | (clampSize(height) - 1)
        if (this.size !== nextSize) {
            this.bytes.push(GS, 0x21, nextSize)
            this.size = nextSize
        }
        const nextSpacing = Math.min(255, 30 * clampSize(height))
        if (this.spacing !== nextSpacing) {
            this.bytes.push(ESC, 0x33, nextSpacing)
            this.spacing = nextSpacing
        }
    }

    line(text: string, align: number, width: number, height: number) {
        this.ensure(align, width, height)
        const limit = columnsFor(width)
        const safe = text.length > limit ? text.slice(0, limit) : text
        this.bytes.push(...encodePc850(safe))
        this.bytes.push(0x0d, LINE_FEED)
    }

    boldLine(text: string, align: number, width: number, height: number) {
        this.bytes.push(ESC, 0x45, 0x01)
        this.line(text, align, width, height)
        this.bytes.push(ESC, 0x45, 0x00)
    }

    lineParts(parts: Array<{ text: string; bold?: boolean }>, align: number, width: number, height: number) {
        this.ensure(align, width, height)
        parts.forEach((part) => {
            if (!part.text) return
            if (part.bold) this.bytes.push(ESC, 0x45, 0x01)
            this.bytes.push(...encodePc850(part.text))
            if (part.bold) this.bytes.push(ESC, 0x45, 0x00)
        })
        this.bytes.push(0x0d, LINE_FEED)
    }

    centered(text: string, width: number, height: number) {
        for (const part of wrapWords(text, columnsFor(width))) this.line(part, 1, width, height)
    }

    pair(left: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const line = left.length + 1 + right.length <= columns
            ? left + ' '.repeat(columns - left.length - right.length) + right
            : `${left.slice(0, columns)}\n${right.slice(-columns).padStart(columns, ' ')}`
        for (const part of line.split('\n')) this.line(part, 0, width, height)
    }

    rule(width: number, height: number) {
        this.line('-'.repeat(columnsFor(width)), 1, width, height)
    }

    item(qty: string, name: string, right: string, width: number, height: number) {
        const columns = columnsFor(width)
        const qtyFieldWidth = Math.min(4, Math.max(2, qty.length + 1))
        const qtyField = `${qty} `.slice(0, qtyFieldWidth).padEnd(qtyFieldWidth, ' ')
        const nameWidth = columns - qtyFieldWidth - (right ? right.length + 1 : 0)
        if (!right || nameWidth < 6) {
            wrapWords(name, Math.max(1, columns - qtyFieldWidth)).forEach((part, index) => {
                this.line(index === 0 ? `${qtyField}${part}` : `${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
            })
            if (right) this.line(right.slice(-columns).padStart(columns, ' '), 0, width, height)
            return
        }
        wrapWords(name, nameWidth).forEach((part, index) => {
            if (index === 0) this.line(`${qtyField}${part.padEnd(nameWidth, ' ')} ${right}`, 0, width, height)
            else this.line(`${' '.repeat(qtyFieldWidth)}${part}`, 0, width, height)
        })
    }

    qr(data: string) {
        const payload = encodePc850(data)
        const storeLength = payload.length + 3
        this.align = -1
        this.bytes.push(ESC, 0x61, 0x01)
        this.align = 1
        this.bytes.push(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00)
        this.bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x05)
        this.bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31)
        this.bytes.push(GS, 0x28, 0x6b, storeLength & 0xff, (storeLength >> 8) & 0xff, 0x31, 0x50, 0x30, ...payload)
        this.bytes.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30)
        this.bytes.push(LINE_FEED)
    }

    finish(beep?: { enabled?: boolean; count?: number }, feeds = 3): Uint8Array {
        for (let i = 0; i < feeds; i++) this.bytes.push(LINE_FEED)
        if (beep?.enabled) {
            const count = Math.min(9, Math.max(1, beep.count || 2))
            this.bytes.push(...BEEP_COMMAND_SIMPLE, count, 1, ...BEEP_COMMAND_ADVANCED, count, 1)
        }
        this.bytes.push(...FULL_CUT)
        return Uint8Array.from(this.bytes)
    }
}
